import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useLayoutEffect, useRef, type MutableRefObject } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  articleLoadFailureReason,
  articleLoadFailureStatus,
  isArticleLoadFailureRetryable,
  type ArticleLoadFailureReason,
} from '../articleLoadFailure';
import { QueryKey } from '@/Config/api/queryKey';
import { ArticleDto, ArticleInputDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

type TagObject = { value: string };

// Sent only when dirty: a normalized-on-load value differs from the DB row without being an edit.
const DIRTY_GATED_FIELDS = ['title', 'content'] as const;

export type ArticleForm = ArticleDto & {
  tagsObjects: TagObject[];
};

export type SaveResult = { editedDuringSave: boolean };

export interface UseArticleSheetReturn {
  form: ReturnType<typeof useForm<ArticleForm>>;
  article: ReturnType<typeof useQuery<ArticleDto>>;
  isArticleUnavailable: boolean;
  loadFailureReason: ArticleLoadFailureReason | null;
  isLoading: boolean;
  isSaving: boolean;
  contentFlushRef: MutableRefObject<(() => void) | null>;
  markLocalEdit: () => void;
  onSubmit: (values: ArticleForm) => void;
  createSubmitHandler: (options?: {
    onSuccess?: (result: SaveResult) => void;
    onError?: (error: unknown) => void;
  }) => (values: ArticleForm) => void;
}

export const useArticleSheet = (): UseArticleSheetReturn => {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id?: string }>();
  const { capture } = usePostHogAnalytics();
  const localEditStartedRef = useRef(false);
  // Published by the rich-text editor: its form updates are debounced, saving is not.
  const contentFlushRef = useRef<(() => void) | null>(null);

  // Article query
  const article = useQuery<ArticleDto>({
    queryKey: [QueryKey.knowledgeBase.article, id],
    staleTime: 0,
    // Retaining the body renders a stale editor: the rich-text sync only accepts a value on initial load.
    gcTime: 0,
    enabled: id != null,
    // Disable structural sharing to ensure new reference on refetch,
    // which triggers the form reset effect
    structuralSharing: false,
  });

  // Article mutation
  const articleMutation = useMutation<
    ArticleDto,
    unknown,
    { id: string } & Partial<ArticleInputDto>
  >({
    mutationKey: [QueryKey.knowledgeBase.article, id],
    onSuccess: (updatedArticle) => {
      capture(PostHogEventEnum.ArticleUpdated, {
        article_id: id,
      });
      queryClient.setQueryData([QueryKey.knowledgeBase.article, id], updatedArticle);
      void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      void queryClient.invalidateQueries({ queryKey: ['knowledge_base'] });
    },
  });

  // Form setup
  const form = useForm<ArticleForm>({
    defaultValues: article.data
      ? {
          ...article.data,
          tagsObjects: (article.data.tags || []).map((t: string) => ({ value: t })),
        }
      : undefined,
  });
  const isFormDirty = form.formState.isDirty;
  const markLocalEdit = useCallback(() => {
    localEditStartedRef.current = true;
  }, []);

  // Track local changes - handled by consumer via form.formState

  // Keep form in sync when article loads/changes
  useLayoutEffect(() => {
    if (!article.data || isFormDirty || localEditStartedRef.current) return;
    form.reset({
      ...article.data,
      tagsObjects: (article.data.tags || []).map((t: string) => ({ value: t })),
    });
  }, [article.data, form, isFormDirty]);

  // Submit handler (base)
  const baseSubmit = useCallback(
    (
      values: ArticleForm,
      callbacks?: { onSuccess?: (result: SaveResult) => void; onError?: (error: unknown) => void },
    ) => {
      const cleanedTags = (values.tagsObjects || [])
        .map((t) => (t.value ?? '').trim())
        .filter((v) => v.length > 0);

      const {
        id: _id,
        created_timestamp: _created,
        downloaded_timestamp: _downloaded,
        last_updated_timestamp: _lastUpdated,
        last_updated_from_external_source_timestamp: _lastUpdatedExternal,
        content_edited_manually_at: _contentEditedManuallyAt,
        state: _state,
        type: _type,
        tagsObjects: _tagsObjects,
        ...rest
      } = values;

      const payload: Partial<ArticleInputDto> = {
        ...rest,
        tags: cleanedTags,
      };
      const { dirtyFields } = form.formState;
      const sentFields = DIRTY_GATED_FIELDS.filter((field) => dirtyFields[field]);
      for (const field of DIRTY_GATED_FIELDS) {
        if (!dirtyFields[field]) delete payload[field];
      }
      // Resending an unchanged list would overwrite tags auto-tagging wrote after this form loaded.
      const baselineTags = values.tags ?? [];
      const tagsUnchanged =
        cleanedTags.length === baselineTags.length &&
        cleanedTags.every((tag, index) => tag === baselineTags[index]);
      if (tagsUnchanged) delete payload.tags;

      if (!id) {
        callbacks?.onError?.(new Error('Missing article id'));
        return;
      }
      // Re-armed here so that on success it only reports input made while the request was in flight.
      localEditStartedRef.current = false;
      articleMutation.mutate(
        { id, ...payload },
        {
          onSuccess: (updatedArticle) => {
            // A toolbar command raises no input event, so land any debounced editor change first.
            contentFlushRef.current?.();
            const editedDuringSave =
              localEditStartedRef.current || !isEqual(form.getValues(), values);
            localEditStartedRef.current = editedDuringSave;
            const retained = form.getValues();
            const savedForm = {
              ...updatedArticle,
              tagsObjects: (updatedArticle.tags || []).map((value) => ({ value })),
            };
            // A field we did not send is unchanged server-side, so its submitted value is its baseline.
            for (const field of DIRTY_GATED_FIELDS) {
              if (!sentFields.includes(field)) savedForm[field] = values[field];
            }
            if (!editedDuringSave) {
              form.reset(savedForm);
              callbacks?.onSuccess?.({ editedDuringSave });
              return;
            }
            // With edits in flight the saved article becomes the baseline they stay dirty against.
            form.reset(savedForm, { keepValues: true });
            // Same rule for the tags baseline, which keepValues also retains.
            if (!tagsUnchanged) form.setValue('tags', savedForm.tags);
            // keepValues drops dirtyFields, which the payload above reads; recompute against the rebase.
            for (const field of DIRTY_GATED_FIELDS) {
              form.setValue(field, retained[field], { shouldDirty: true });
            }
            callbacks?.onSuccess?.({ editedDuringSave });
          },
          onError: (error) => {
            callbacks?.onError?.(error);
          },
        },
      );
    },
    [articleMutation, form],
  );

  // Public submit variants
  const onSubmit = useCallback((values: ArticleForm) => baseSubmit(values), [baseSubmit]);
  const createSubmitHandler = useCallback(
    (options?: { onSuccess?: (result: SaveResult) => void; onError?: (error: unknown) => void }) =>
      (values: ArticleForm) =>
        baseSubmit(values, options),
    [baseSubmit],
  );

  const loadFailureReason = article.isError ? articleLoadFailureReason(article.error) : null;
  const loadFailureStatus = article.isError ? articleLoadFailureStatus(article.error) : null;
  // A 4xx is settled before the request leaves the browser, so a previous successful load's
  // data must not keep the editor alive over it: the server will not accept that row back.
  const hasDeterministicLoadError =
    loadFailureReason !== null && !isArticleLoadFailureRetryable(loadFailureReason);
  const isArticleUnavailable = article.isError && (!article.data || hasDeterministicLoadError);
  const isLoading = isArticleUnavailable || !article.data;
  const isSaving = articleMutation.isPending;

  // React Query catches the queryFn rejection, so PostHog's capture_exceptions -- which only
  // sees uncaught errors -- never fired for this. A user hit a permanently failing article
  // sheet in production and we could see the rageclicks but not the status code.
  //
  // `status_code` rides along with the bounded `reason`, matching `article_upload_failed`:
  // the reason alone cannot separate a 404 from a 422, and that is exactly the distinction
  // the one incident we have turns on.
  //
  // Keyed on errorUpdatedAt so this is one event per failed attempt: a retry that fails the
  // same way reports again, which is the signal that a retry is not helping. Re-renders do
  // not move any dependency, so it stays one event per attempt rather than one per render.
  useEffect(() => {
    if (!loadFailureReason || !id) return;
    capture(PostHogEventEnum.ArticleLoadFailed, {
      article_id: id,
      reason: loadFailureReason,
      status_code: loadFailureStatus,
      retryable: isArticleLoadFailureRetryable(loadFailureReason),
    });
  }, [loadFailureReason, loadFailureStatus, id, capture, article.errorUpdatedAt]);

  return {
    form,
    article,
    isArticleUnavailable,
    loadFailureReason,
    isLoading,
    isSaving,
    contentFlushRef,
    markLocalEdit,
    onSubmit,
    createSubmitHandler,
  };
};
